import { use, useEffect, useMemo, useRef } from 'react'

import { useNavigate } from 'react-router-dom'

import { fetchConversation } from '@app/api'
import config from '@app/config'
import { ChatContext } from '@app/context'
import { getConversationById, getUnreadStatusEnabled } from '@app/helpers'
import { useUser } from '@app/hooks'
import { ChatState, ExistThreadEventCapturedPayload } from '@app/hooks'
import { useConversationID } from '@app/hooks'
import { useDependencies } from '@app/hooks'
import { Participant } from '@app/models'
import {
  closeConversationAndNavigateToMessages,
  handleForbiddenConversationError,
} from '@app/utils'
import { isImpersonatedB2BSession } from '@app/utils/token/isImpersonatedB2BSession'

import { logger } from '@lib/utils'

import { createOnChatMessageReceived } from './handlers/onChatMessageReceived'
import { createOnChatParticipantsAdded } from './handlers/onChatParticipantsAdded'
import { createOnChatThreadCreated } from './handlers/onChatThreadCreated'
import { tokenRefresher } from './tokenRefresher'

import type {
  AzureChatClient,
  AzureSdkModules,
  ThreadMessageReceivedEvent,
  ThreadParticipantsAddedEvent,
} from './types'

import type { CommunicationTokenRefreshOptions } from '@azure/communication-common'

let azureSdkModulesPromise: Promise<AzureSdkModules> | null = null

// Lazily load the Azure Communication SDK to keep the initial bundle smaller.
const loadAzureSdk = async (): Promise<AzureSdkModules> => {
  if (!azureSdkModulesPromise) {
    azureSdkModulesPromise = Promise.all([
      import('@azure/communication-chat'),
      import('@azure/communication-common'),
    ]).then(([chatModule, commonModule]) => ({
      ChatClient: chatModule.ChatClient,
      AzureCommunicationTokenCredential:
        commonModule.AzureCommunicationTokenCredential,
    }))
  }

  return azureSdkModulesPromise
}

export const useAzureClient = () => {
  const navigate = useNavigate()
  const currentConversationId = useConversationID()
  const { state, dispatch } = use(ChatContext)

  const resolvedConversationId = useMemo(() => {
    const conversation = getConversationById(
      state.conversations,
      currentConversationId ?? undefined
    )

    if (conversation) return conversation.id
    return typeof currentConversationId === 'number'
      ? currentConversationId
      : null
  }, [state.conversations, currentConversationId])

  const refCurrentConversationId = useRef(resolvedConversationId)
  const refReceivedMessages = useRef<Set<string>>(new Set())

  const userState = useUser()

  const refState = useRef<ChatState>(state)

  const userLanguage = config.regionalSettings?.language
  const isActAs = isImpersonatedB2BSession()
  const onConversationForbidden = () => {
    closeConversationAndNavigateToMessages(navigate)
  }

  const { getDependencies } = useDependencies(refState)
  const getReceivedUnreadStatus = (
    conversationId: number | null,
    participants: Participant[]
  ): boolean => {
    if (!getUnreadStatusEnabled(userState)) {
      return false
    }
    if (
      !isActAs &&
      conversationId === refCurrentConversationId.current
    ) {
      return false
    }

    return participants.some((item) => state.participantIds.includes(item.id))
  }

  const forceUpdateConversation = async (threadId: string): Promise<void> => {
    try {
      const payload = await fetchConversation(threadId)

      dispatch({
        type: 'BACKGROUND_CONVERSATION_UPDATE_SUCCESS',
        payload,
      })
    } catch (error) {
      if (
        handleForbiddenConversationError(error, () => {
          const conversation = getConversationById(
            refState.current.conversations,
            threadId
          )

          if (conversation?.id === refCurrentConversationId.current) {
            onConversationForbidden()
          }
        })
      ) {
        return
      }

      dispatch({
        type: 'BACKGROUND_CONVERSATION_UPDATE_ERROR',
        payload: threadId,
      })
    }
  }

  const readConversation = async (
    threadId: string,
    participantId: string
  ): Promise<void> => {
    logger('onConversationReadReceived', { threadId, participantId }, '#c203fc')
    const conversation = getConversationById(
      refState.current.conversations,
      threadId
    )

    const participant = conversation?.participants.find(
      (item) => item.id.toString() === participantId
    )

    if (conversation && userState.currentUser.id === participant?.userId) {
      conversation.unread = false

      const payload: ExistThreadEventCapturedPayload = {
        conversation: {
          ...conversation,
          unread: false,
        },
      }

      dispatch({
        payload,
        type: 'EXIST_THREAD_EVENT_CAPTURED',
      })
    }
  }

  const onChatMessageReceived = createOnChatMessageReceived({
    refState,
    refCurrentConversationId,
    refReceivedMessages,
    currentUserId: userState.currentUser?.id,
    userLanguage,
    dispatch,
    getDependencies,
    getReceivedUnreadStatus,
    forceUpdateConversation,
    readConversation,
    onConversationForbidden,
  })

  const onChatThreadCreated = createOnChatThreadCreated({
    dispatch,
    getDependencies,
    unreadEnabled: getUnreadStatusEnabled(userState),
    currentUserId: userState.currentUser?.id,
    onConversationForbidden,
  })

  const onChatParticipantsAdded = createOnChatParticipantsAdded({
    dispatch,
    refState,
    getDependencies,
    getReceivedUnreadStatus,
    onConversationForbidden,
  })

  let client: AzureChatClient | null = null

  const subscribe = async (token: string) => {
    if (client) {
      return
    }

    const { ChatClient, AzureCommunicationTokenCredential } =
      await loadAzureSdk()

    const tokenRefreshOptions = {
      token: token,
      tokenRefresher: tokenRefresher,
    } as CommunicationTokenRefreshOptions

    const credential = new AzureCommunicationTokenCredential(
      tokenRefreshOptions
    )

    const sdkUrl = import.meta.env.VITE_SDK_URL || config.variables?.acsChatUrl

    if (!sdkUrl) {
      logger('ACS SDK URL is missing in environment configuration', null, 'red')
      return
    }

    client = new ChatClient(sdkUrl, credential)

    await client.startRealtimeNotifications()

    client.on('chatMessageReceived', (event) => {
      onChatMessageReceived(event as ThreadMessageReceivedEvent)
    })

    client.on('participantsAdded', (event) => {
      onChatParticipantsAdded(event as ThreadParticipantsAddedEvent)
    })

    client.on('chatThreadCreated', (event) => {
      onChatThreadCreated(event)
    })
  }

  useEffect(() => {
    refState.current = state
  }, [state])

  useEffect(() => {
    refCurrentConversationId.current = resolvedConversationId
  }, [resolvedConversationId])

  useEffect(() => {
    if (state.acsChatToken.token) {
      subscribe(state.acsChatToken.token).catch((error) =>
        logger('ACS Subscribe Error', error, 'red')
      )
    }
  }, [state.acsChatToken.token, isActAs])
}
